import type { GraphicsPreset } from '@il-colpo/shared/settings';

export interface DeviceInfo {
  cores: number;
  /** `navigator.deviceMemory` in GB (Chromium only, capped at 8). */
  memoryGb: number | undefined;
  /** Touch-first device (phone/tablet). */
  coarsePointer: boolean;
  /** `null` when WebGL is unavailable, otherwise the unmasked renderer (may be empty). */
  gpuRenderer: string | null;
}

const SOFTWARE_RENDERER = /swiftshader|llvmpipe|software|basic render|softpipe/i;

/**
 * Picks a starting preset from coarse device facts. `auto` never returns `ultra`:
 * that level needs a measured benchmark (milestone 4) or a manual choice.
 */
export function detectPreset(info: DeviceInfo): GraphicsPreset {
  if (info.gpuRenderer === null) return 'low';
  if (SOFTWARE_RENDERER.test(info.gpuRenderer)) return 'low';
  const memory = info.memoryGb ?? (info.coarsePointer ? 4 : 8);
  if (info.coarsePointer) {
    if (memory <= 3 || info.cores <= 4) return 'low';
    if (memory >= 6 && info.cores >= 8) return 'high';
    return 'medium';
  }
  if (memory >= 8 && info.cores >= 8) return 'high';
  if (memory >= 4 || info.cores >= 4) return 'medium';
  return 'low';
}

interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number;
}

function readGpuRenderer(): string | null {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = ext ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as unknown) : '';
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return typeof renderer === 'string' ? renderer : '';
  } catch {
    return null;
  }
}

export function readDeviceInfo(): DeviceInfo {
  const nav = navigator as NavigatorWithMemory;
  return {
    cores: nav.hardwareConcurrency || 4,
    memoryGb: nav.deviceMemory,
    coarsePointer: typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
    gpuRenderer: readGpuRenderer(),
  };
}
